import React, { useEffect, useState } from 'react'
import citiesData from '../../src/data/cities.json'
import useRegisterValidators from '../validators/register'
import * as Yup from 'yup'
import { UserData, registerUser } from '../../services/register'
import { FormError } from './form-error'
import { FormSuccess } from './form-success'
import { useTranslation } from 'react-i18next'
import MyButton from './buttons'
import MyDate from './MyDate'
import MyInput from './MyInput'
import MySelect from './MySelect'
import {
  FaCalendarAlt,
  FaCity,
  FaEnvelope,
  FaHome,
  FaUnlockAlt,
  FaUser,
  FaLock,
  FaInfoCircle,
} from 'react-icons/fa'
import { Link, useNavigate } from 'react-router-dom'
import { parse, format, isValid } from 'date-fns'
import StatusSelection from './StatusSelection'
import MyCheckbox from './MyCheckbox'
import { ScreenMode, ScreenModeType } from '../pages/LoginRegisterPages'
import { ArrowLeft, UserPlus } from 'lucide-react'

interface CityOption {
  label: string
  value: string
}

interface RegisterFormProps {
  onSwitchMode: (mode: ScreenModeType) => void
  initialStatus?: string
  email?: string
  setEmail?: React.Dispatch<React.SetStateAction<string>>
  invalidFields?: Set<string>
  phone?: string
  setInvalidFields?: React.Dispatch<React.SetStateAction<Set<string>>>
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchMode }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [city, setCity] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [birthday, setBirthday] = useState<string>('')
  const [nickname, setNickname] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [isFullScreen, setIsFullScreen] = useState(false)

  const [password, setPassword] = useState<string>('')
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('')
  const [showPassword] = useState(false)
  const [phone] = useState<string>('')
  const [showPasswordConfirmation] = useState(false)
  const [status, setStatus] = useState<string>('')
  const [statusSelected, setStatusSelected] = useState<boolean>(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsError, setTermsError] = useState('')
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null)
  const [invalidFields, setInvalidFields] = useState<{ [key: string]: string }>(
    {},
  )

  const [vatNumber] = useState('')
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(false)
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 0)

    return () => clearTimeout(timer)
  }, [])

  const options: CityOption[] = citiesData.flatMap(province =>
    province.cities.map(city => ({
      value: city.postal_code,
      label: `${city.postal_code}, ${city.city_fr}`,
    })),
  )

  const handleBackToLogin = () => {
    if (!statusSelected) {
      onSwitchMode(ScreenMode.SIGN_IN)
    } else {
      setStatusSelected(false)
    }
  }

  const schema = useRegisterValidators()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSuccessMessage('')
    setErrorMessage('')
    setTermsError('')
    setInvalidFields({})

    if (!termsAccepted) {
      setTermsError(t('please-accept-terms'))
      return
    }

    try {
      const normalizedEmail = email.trim().toLowerCase()
      await schema.validate(
        {
          birthday,
          nickname,
          email: normalizedEmail,
          city,
          zipCode,
          password,
          passwordConfirmation,
          status,
        },
        { abortEarly: false },
      )

      const parsedBirthday = parse(birthday, 'dd-MM-yyyy', new Date())
      if (!isValid(parsedBirthday)) {
        throw new Error(t('invalid-birthday'))
      }
      const formattedDate = format(parsedBirthday, 'yyyy-MM-dd')

      const formData: UserData = {
        firstname: '',
        lastname: '',
        birthday: formattedDate,
        nickname,
        email: normalizedEmail,
        password,
        address: {
          street: '',
          number: '',
          city,
          zip_code: Number(zipCode),
        },
        status: [status],
        tva: vatNumber,
        phone: '',
      }

      if (phone) {
        formData.phone = phone
      }

      const registrationResult = await registerUser(formData)

      if (registrationResult.error) {
        setErrorMessage(registrationResult.error)
      } else {
        setSuccessMessage(registrationResult.success)
        setTimeout(() => {
          navigate('/')
        }, 5000)
      }
    } catch (validationErrors) {
      if (validationErrors instanceof Yup.ValidationError) {
        const newInvalidFields: { [key: string]: string } = {}

        validationErrors.inner.forEach(error => {
          if (error.path) {
            newInvalidFields[error.path] = error.message
          }
        })

        setInvalidFields(newInvalidFields)
      } else {
        setErrorMessage(t('unexpected-error'))
        console.error('Unexpected error:', validationErrors)
      }
    }
  }

  const handlePasswordChange = (value: string) => {
    setPassword(value)
  }

  const handlePasswordConfirmationChange = (value: string) => {
    setPasswordConfirmation(value)
  }

  const handleCityChange = (value: string) => {
    const selectedOption = options.find(option => option.value === value)
    if (selectedOption) {
      const [postalCode, cityName] = selectedOption.label.split(', ')
      setZipCode(postalCode)
      setCity(cityName)
      setSelectedCity(selectedOption)
    } else {
      setZipCode('')
      setCity('')
      setSelectedCity(null)
    }
  }

  const handleStatusChange = (status: string) => {
    setStatus(status)
  }

  const handleSubmitClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const form = e.currentTarget.closest('form')
    if (form) {
      form.requestSubmit()
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className={`${
          isFullScreen
            ? 'fixed inset-0 bg-white z-50 flex flex-col justify-center px-6 overflow-auto w-full h-full'
            : 'w-full relative space-y-4 p-2 px-4 md:px-8 max-w-2xl mx-auto'
        }`}
      >
        {!statusSelected ? (
          <>
            <StatusSelection
              status={status}
              handleStatusChange={handleStatusChange}
              onSwitchMode={onSwitchMode}
              setStatusSelected={setStatusSelected}
              isFullScreen={isFullScreen}
              setIsFullScreen={setIsFullScreen}
            />
          </>
        ) : (
          <>
            <div
              className={`transition-opacity duration-700 ease-in-out ${
                isVisible ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div
                className={`${
                  isVisible ? 'opacity-100' : 'opacity-0'
                } transition-opacity duration-700 ease-in-out`}
              >
                <div className='space-y-4 sm:space-y-5 text-center '>
                  <div className='w-full'>
                    <MyDate
                      label={t('create-ad.birthday-dd-mm-yyyy')}
                      value={birthday}
                      onChange={newValue => setBirthday(newValue)}
                      icon={<FaCalendarAlt />}
                      isInvalid={!!invalidFields['birthday']}
                      error={invalidFields['birthday']}
                    />
                  </div>
                  <div className='w-full'>
                    <MyInput
                      label={t('create-ad.nickname')}
                      value={nickname}
                      onChange={setNickname}
                      className='date-input placeholder-right'
                      icon={<FaUser />}
                      isInvalid={!!invalidFields['nickname']}
                      error={invalidFields['nickname']}
                    />
                  </div>
                  <div className='w-full'>
                    <MyInput
                      label={t('email')}
                      type='email'
                      value={email}
                      onChange={value => setEmail(value.trim().toLowerCase())}
                      icon={<FaEnvelope />}
                      isInvalid={!!invalidFields['email']}
                      error={invalidFields['email']}
                    />
                  </div>
                  <div className='w-full'>
                    <MySelect
                      label={t('city-postal-code')}
                      options={options}
                      value={selectedCity ? selectedCity.label : ''}
                      onChange={handleCityChange}
                      icon={<FaCity />}
                      isInvalid={!!invalidFields['city']}
                      error={invalidFields['city']}
                    />
                  </div>
                  <div className='w-full rounded-lg bg-grisclair p-2 text-xs flex justify-center'>
                    <FaInfoCircle className='pr-2 text-base w-5 h-5' />{' '}
                    <span className='text-xs'>{t('password-info')}</span>
                  </div>
                  <div className='w-full'>
                    <MyInput
                      label={t('password')}
                      type='password'
                      value={password}
                      onChange={handlePasswordChange}
                      icon={<FaLock />}
                      isInvalid={!!invalidFields['password']}
                      error={invalidFields['password']}
                    />
                  </div>
                  <div className='w-full'>
                    <MyInput
                      label={t('confirm-password')}
                      showClearButton={false}
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      value={passwordConfirmation}
                      onChange={handlePasswordConfirmationChange}
                      className='password-confirmation-input'
                      icon={<FaUnlockAlt />}
                      isInvalid={!!invalidFields['password-confirmation']}
                      error={invalidFields['password-confirmation']}
                    />
                  </div>
                  <div className='flex justify-around bg-grisclair rounded px-2 sm:px-4 py-2'>
                    <MyCheckbox
                      name='termsAccepted'
                      checked={termsAccepted}
                      variant='login'
                      className='w-full'
                      onChange={checked => {
                        setTermsAccepted(checked)
                        if (checked) setTermsError('')
                      }}
                      label={
                        <span className='text-xs sm:text-sm'>
                          {t('i-accept-the')}{' '}
                          <a
                            href='/terms-of-use/'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-redpink hover:underline'
                          >
                            {t('terms-and-conditions')}
                          </a>
                        </span>
                      }
                      isInvalid={!!termsError}
                    />
                  </div>
                  {termsError && <FormError message={t('accept-terms')} />}
                  {successMessage && <FormSuccess message={t('check-email')} />}
                  {errorMessage && <FormError message={t('form-error')} />}
                  <div className='w-full rounded-lg bg-yellow-100 border border-yellow-300 text-yellow-800 p-1 text-xs text-center'>
                    {t('phone-validation-warning')}
                  </div>

                  <div className='flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4'>
                    <MyButton
                      variant='anthracite'
                      className='w-full sm:w-auto mt-4 text-sm sm:text-base cursor-pointer py-3 rounded flex items-center justify-center gap-2'
                      onClick={handleBackToLogin}
                    >
                      <ArrowLeft className='w-4 h-4' />
                      {t('status-select')}
                    </MyButton>

                    <MyButton
                      onClick={handleSubmitClick}
                      variant='green'
                      className='w-full sm:w-auto  hover:bg-anthracite transition-all flex items-center justify-center gap-2'
                    >
                      {t('register')}
                      <UserPlus className='w-4 h-4' />
                    </MyButton>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </form>
    </>
  )
}

export default RegisterForm
